import { useState } from 'react';
import { categories } from '@/mocks/categories';

const CategorySection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleSelect = (namePl: string, link: string) => {
    setSelectedCategory(namePl);
    setIsOpen(false);
    window.open(link, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="w-full bg-background-50 py-10 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-10">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-3 md:mb-4">
            Przeglądaj Kategorie <span className="text-primary-500">Kakobuy Spreadsheet</span>
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            Wybierz kategorię z rozwijanej listy lub kliknij jeden z przycisków poniżej, aby przejść bezpośrednio do interesujących Cię produktów w arkuszu Kakobuy.
          </p>
        </div>

        {/* Dropdown */}
        <div className="flex justify-center mb-6 md:mb-10">
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between bg-background-100 border border-background-200 rounded-xl px-4 md:px-5 py-3 md:py-3.5 text-foreground-800 hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <span className={selectedCategory ? 'text-foreground-900 font-medium text-sm md:text-base' : 'text-foreground-500 text-sm md:text-base'}>
                {selectedCategory || 'Wybierz kategorię Kakobuy Spreadsheet...'}
              </span>
              {isOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-500 transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-500 transition-transform duration-200"></i>
              )}
            </button>

            {isOpen && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-background-200 rounded-xl shadow-lg max-h-72 md:max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat.namePl, cat.link)}
                    className="w-full flex items-center gap-3 px-4 md:px-5 py-3 text-left text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                    </span>
                    <span className="text-sm font-medium">{cat.namePl}</span>
                    <span className="ml-auto text-xs text-foreground-400">{cat.nameEn}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Horizontal scrollable category chips */}
        <div className="md:hidden flex overflow-x-auto gap-2 pb-2 mb-4 scrollbar-hide -mx-1 px-1">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 flex items-center gap-1.5 bg-background-100 border border-background-200 hover:border-primary-200 rounded-full px-3.5 py-2 transition-all duration-200 cursor-pointer active:scale-95"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-primary-500 text-xs`}></i>
              </span>
              <span className="text-xs font-medium text-foreground-800 whitespace-nowrap">
                {cat.namePl}
              </span>
            </a>
          ))}
        </div>

        {/* Desktop: Category grid */}
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-3">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-2 bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 rounded-xl px-4 py-4 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
            >
              <span className="w-8 h-8 flex items-center justify-center">
                <i className={`${cat.icon} text-lg text-primary-500 group-hover:text-primary-600 transition-colors`}></i>
              </span>
              <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-600 whitespace-nowrap transition-colors">
                {cat.namePl}
              </span>
            </a>
          ))}
        </div>

        {/* Mobile: Compact 3-column grid below the scroll chips */}
        <div className="md:hidden grid grid-cols-3 gap-2">
          {categories.slice(0, 12).map((cat) => (
            <a
              key={cat.id}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-1.5 bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 rounded-lg px-2 py-3 transition-all duration-200 cursor-pointer active:scale-95"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className={`${cat.icon} text-base text-primary-500 group-hover:text-primary-600 transition-colors`}></i>
              </span>
              <span className="text-[11px] font-medium text-foreground-800 group-hover:text-primary-600 whitespace-nowrap transition-colors text-center leading-tight">
                {cat.namePl}
              </span>
            </a>
          ))}
          {/* Remaining 2 items span full width */}
          {categories.slice(12).map((cat) => (
            <a
              key={cat.id}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="col-span-3 group flex items-center justify-center gap-1.5 bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 rounded-lg px-3 py-2.5 transition-all duration-200 cursor-pointer active:scale-95"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} text-sm text-primary-500 group-hover:text-primary-600 transition-colors`}></i>
              </span>
              <span className="text-[11px] font-medium text-foreground-800 group-hover:text-primary-600 whitespace-nowrap transition-colors">
                {cat.namePl}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategorySection;